import React, { useState, useEffect, useRef } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useCompany } from "../context/CompanyContext";
import {
  Users,
  Home,
  Upload,
  DollarSign,
  LogOut,
  User,
  Building2,
  Calendar,
  UserPlus,
  UserCheck,
  Menu,
  X,
  Shield,
  ChevronDown,
  BarChart3,
  Briefcase,
  Key,
  Calculator,
  FileSpreadsheet,
  BookOpen,
  Terminal,
} from "lucide-react";
import ChangePasswordModal from "./ChangePasswordModal";
import MonthSelectionModal from "./MonthSelectionModal";
import "../styles/Navbar.css";

const Navbar = () => {
  const { user, logout, hasPermission } = useAuth();
  const { selectedCompany, switchCompany, companies } = useCompany();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [showCompanySwitcher, setShowCompanySwitcher] = useState(false);
  const [openDropdown, setOpenDropdown] = useState(null);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [showMonthModal, setShowMonthModal] = useState(false);
  const [pendingNavigation, setPendingNavigation] = useState(null);
  const logoRef = useRef(null);
  const dropdownRefs = useRef({});

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (logoRef.current && !logoRef.current.contains(event.target)) {
        setShowCompanySwitcher(false);
      }

      // Close nav dropdowns when clicking outside
      let clickedInside = false;
      Object.values(dropdownRefs.current).forEach((ref) => {
        if (ref && ref.contains(event.target)) {
          clickedInside = true;
        }
      });

      if (!clickedInside) {
        setOpenDropdown(null);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  const isActive = (path) => {
    return location.pathname === path ? "active" : "";
  };

  const toggleMobileMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  const closeMobileMenu = () => {
    setMobileMenuOpen(false);
    setOpenDropdown(null);
  };

  const toggleDropdown = (dropdownName) => {
    setOpenDropdown(openDropdown === dropdownName ? null : dropdownName);
  };

  const isDropdownActive = (paths) => {
    return paths.some((path) => location.pathname === path) ? "active" : "";
  };

  const handleCompanySwitch = (partnershipId) => {
    switchCompany(partnershipId);
    setShowCompanySwitcher(false);
    // switchCompany already reloads the page
  };

  const toggleCompanySwitcher = (e) => {
    e.preventDefault();
    setShowCompanySwitcher(!showCompanySwitcher);
  };

  const handleNavWithMonthSelection = (e, path, title) => {
    e.preventDefault();
    setPendingNavigation({ path, title });
    setShowMonthModal(true);
    closeMobileMenu();
  };

  const handleMonthSelected = (month) => {
    if (pendingNavigation) {
      navigate(`${pendingNavigation.path}?month=${month}`);
      setPendingNavigation(null);
    }
    setShowMonthModal(false);
  };

  return (
    <nav className='navbar'>
      <div className='navbar-container'>
        <div className='navbar-brand'>
          <div
            ref={logoRef}
            className='logo'
            onClick={toggleCompanySwitcher}
            title='החלף חברה'
          >
            <img
              src={selectedCompany.logo}
              alt={selectedCompany.name}
              className='logo-image'
            />
            <ChevronDown
              size={18}
              className={`logo-chevron ${showCompanySwitcher ? "rotated" : ""}`}
            />
            {showCompanySwitcher && (
              <div
                className='company-switcher-dropdown-logo'
                onClick={(e) => e.stopPropagation()}
              >
                {companies && companies.length > 0 ? (
                  companies.map((partnership) => (
                    <button
                      key={partnership.id}
                      className={`company-option ${
                        selectedCompany.id === partnership.id ? "active" : ""
                      }`}
                      onClick={() => handleCompanySwitch(partnership.id)}
                    >
                      <img
                        src={partnership.logo}
                        alt={partnership.name}
                        className='company-logo-small'
                        onError={(e) => {
                          e.target.style.display = "none";
                        }}
                      />
                      <span className='company-name'>
                        {partnership.displayName}
                      </span>
                      {selectedCompany.id === partnership.id && (
                        <span className='check-mark'>✓</span>
                      )}
                    </button>
                  ))
                ) : (
                  <div className='company-option'>
                    <span>Loading partnerships...</span>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        <button className='mobile-menu-toggle' onClick={toggleMobileMenu}>
          {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
        </button>

        <div className={`navbar-menu ${mobileMenuOpen ? "mobile-open" : ""}`}>
          {/* Hide all navigation for salary_viewer, recruiter, and payer users */}
          {user?.permission !== "salary_viewer" &&
            user?.permission !== "recruiter" &&
            user?.permission !== "payer" && (
              <>
                {/* Dashboards Dropdown */}
                <div
                  className={`nav-dropdown ${isDropdownActive([
                    "/",
                    "/dashboard/advanced",
                  ])}`}
                  ref={(el) => (dropdownRefs.current.dashboards = el)}
                >
                  <button
                    className='nav-link dropdown-toggle'
                    onClick={() => toggleDropdown("dashboards")}
                  >
                    <BarChart3 size={18} />
                    <span>דשבורדים</span>
                    <ChevronDown
                      size={16}
                      className={`chevron ${
                        openDropdown === "dashboards" ? "open" : ""
                      }`}
                    />
                  </button>
                  {openDropdown === "dashboards" && (
                    <div className='dropdown-menu'>
                      <Link
                        to='/'
                        className={`dropdown-item ${isActive("/")}`}
                        onClick={closeMobileMenu}
                      >
                        <Home size={16} />
                        <span>לוח בקרה ראשי</span>
                      </Link>
                      {hasPermission("owner") && (
                        <Link
                          to='/dashboard/advanced'
                          className={`dropdown-item ${isActive(
                            "/dashboard/advanced",
                          )}`}
                          onClick={closeMobileMenu}
                        >
                          <BarChart3 size={16} />
                          <span>דשבורד מתקדם</span>
                        </Link>
                      )}
                    </div>
                  )}
                </div>

                {/* Management Dropdown */}
                <div
                  className={`nav-dropdown ${isDropdownActive([
                    "/employees",
                    "/customers",
                    "/users",
                    "/partnerships",
                  ])}`}
                  ref={(el) => (dropdownRefs.current.management = el)}
                >
                  <button
                    className='nav-link dropdown-toggle'
                    onClick={() => toggleDropdown("management")}
                  >
                    <Users size={18} />
                    <span>ניהול</span>
                    <ChevronDown
                      size={16}
                      className={`chevron ${
                        openDropdown === "management" ? "open" : ""
                      }`}
                    />
                  </button>
                  {openDropdown === "management" && (
                    <div className='dropdown-menu'>
                      <Link
                        to='/employees'
                        className={`dropdown-item ${isActive("/employees")}`}
                        onClick={closeMobileMenu}
                      >
                        <Users size={16} />
                        <span>עובדים</span>
                      </Link>
                      <Link
                        to='/customers'
                        className={`dropdown-item ${isActive("/customers")}`}
                        onClick={closeMobileMenu}
                      >
                        <Building2 size={16} />
                        <span>לקוחות</span>
                      </Link>
                      {hasPermission("owner") && (
                        <Link
                          to='/partnerships'
                          className={`dropdown-item ${isActive(
                            "/partnerships",
                          )}`}
                          onClick={closeMobileMenu}
                        >
                          <Briefcase size={16} />
                          <span>שותפויות</span>
                        </Link>
                      )}
                      {hasPermission("owner") && (
                        <Link
                          to='/profit'
                          className={`dropdown-item ${isActive("/profit")}`}
                          onClick={closeMobileMenu}
                        >
                          <Calculator size={16} />
                          <span>חישוב רווח</span>
                        </Link>
                      )}
                      {(hasPermission("admin") || hasPermission("owner")) && (
                        <Link
                          to='/users'
                          className={`dropdown-item ${isActive("/users")}`}
                          onClick={closeMobileMenu}
                        >
                          <UserPlus size={16} />
                          <span>ניהול משתמשים</span>
                        </Link>
                      )}
                      {(hasPermission("admin") || hasPermission("owner")) && (
                        <Link
                          to='/employee-approval'
                          className={`dropdown-item ${isActive(
                            "/employee-approval",
                          )}`}
                          onClick={closeMobileMenu}
                        >
                          <UserCheck size={16} />
                          <span>אישור עובדים חדשים</span>
                        </Link>
                      )}
                    </div>
                  )}
                </div>

                {/* Attendance & Enforcement Dropdown */}
                <div
                  className={`nav-dropdown ${isDropdownActive([
                    "/presence",
                    "/enforcement",
                  ])}`}
                  ref={(el) => (dropdownRefs.current.attendance = el)}
                >
                  <button
                    className='nav-link dropdown-toggle'
                    onClick={() => toggleDropdown("attendance")}
                  >
                    <Calendar size={18} />
                    <span>נוכחות ותגבור</span>
                    <ChevronDown
                      size={16}
                      className={`chevron ${
                        openDropdown === "attendance" ? "open" : ""
                      }`}
                    />
                  </button>
                  {openDropdown === "attendance" && (
                    <div className='dropdown-menu'>
                      <a
                        href='/presence'
                        className={`dropdown-item ${isActive("/presence")}`}
                        onClick={(e) =>
                          handleNavWithMonthSelection(
                            e,
                            "/presence",
                            "נוכחות - בחר חודש",
                          )
                        }
                      >
                        <Calendar size={16} />
                        <span>נוכחות</span>
                      </a>
                      <Link
                        to='/enforcement'
                        className={`dropdown-item ${isActive("/enforcement")}`}
                        onClick={closeMobileMenu}
                      >
                        <Shield size={16} />
                        <span>תגבור</span>
                      </Link>
                    </div>
                  )}
                </div>

                {/* Salaries Dropdown */}
                {(hasPermission("site_admin") ||
                  hasPermission("admin") ||
                  hasPermission("owner")) && (
                  <div
                    className={`nav-dropdown ${isDropdownActive([
                      "/salaries",
                      "/salaries/company",
                      "/salaries/both",
                    ])}`}
                    ref={(el) => (dropdownRefs.current.salaries = el)}
                  >
                    <button
                      className='nav-link dropdown-toggle'
                      onClick={() => toggleDropdown("salaries")}
                    >
                      <DollarSign size={18} />
                      <span>משכורות</span>
                      <ChevronDown
                        size={16}
                        className={`chevron ${
                          openDropdown === "salaries" ? "open" : ""
                        }`}
                      />
                    </button>
                    {openDropdown === "salaries" && (
                      <div className='dropdown-menu'>
                        <a
                          href='/salaries'
                          className={`dropdown-item ${isActive("/salaries")}`}
                          onClick={(e) =>
                            handleNavWithMonthSelection(
                              e,
                              "/salaries",
                              "משכורות קבלנים - בחר חודש",
                            )
                          }
                        >
                          <DollarSign size={16} />
                          <span>משכורות קבלנים</span>
                        </a>
                        <a
                          href='/salaries/company'
                          className={`dropdown-item ${isActive(
                            "/salaries/company",
                          )}`}
                          onClick={(e) =>
                            handleNavWithMonthSelection(
                              e,
                              "/salaries/company",
                              "משכורות חברה - בחר חודש",
                            )
                          }
                        >
                          <Briefcase size={16} />
                          <span>משכורות חברה</span>
                        </a>
                        <a
                          href='/salaries/both'
                          className={`dropdown-item ${isActive(
                            "/salaries/both",
                          )}`}
                          onClick={(e) =>
                            handleNavWithMonthSelection(
                              e,
                              "/salaries/both",
                              "משכורות קבלן וחברה - בחר חודש",
                            )
                          }
                        >
                          <Users size={16} />
                          <span>משכורות קבלן וחברה</span>
                        </a>
                      </div>
                    )}
                  </div>
                )}

                {/* Upload Link */}
                {hasPermission("admin") && (
                  <Link
                    to='/upload'
                    className={`nav-link ${isActive("/upload")}`}
                    onClick={closeMobileMenu}
                  >
                    <Upload size={18} />
                    <span>העלאת קבצים</span>
                  </Link>
                )}

                {/* Logs Link - Admin only - temporarily disabled
                {hasPermission("admin") && (
                  <Link
                    to='/logs'
                    className={`nav-link ${isActive("/logs")}`}
                    onClick={closeMobileMenu}
                  >
                    <Terminal size={18} />
                    <span>לוגים</span>
                  </Link>
                )}
                */}

                {/* Excel Report - Show for all partnerships */}
                {hasPermission("admin") && (
                  <Link
                    to='/report'
                    className={`nav-link ${
                      isActive("/report") ||
                      isActive("/gold-clean-report") ||
                      isActive("/koral-report")
                    }`}
                    onClick={closeMobileMenu}
                  >
                    <FileSpreadsheet size={18} />
                    <span>דוח אקסל</span>
                  </Link>
                )}

                {/* Docs link - visible to all */}
                <Link
                  to='/docs'
                  className={`nav-link ${isActive("/docs")}`}
                  onClick={closeMobileMenu}
                >
                  <BookOpen size={18} />
                  <span>מדריך שימוש</span>
                </Link>
              </>
            )}
        </div>

        <div className='navbar-user'>
          <div
            className='user-info'
            onClick={() => setShowPasswordModal(true)}
            style={{ cursor: "pointer" }}
            title='לחץ לשינוי סיסמה'
          >
            <User size={16} />
            <span className='username'>{user?.username}</span>
            <span className='user-badge'>
              {user?.permission === "viewer" && "צופה"}
              {user?.permission === "salary_viewer" && "משכורות"}
              {user?.permission === "recruiter" && "מגייס"}
              {user?.permission === "payer" && "משלם"}
              {user?.permission === "site_admin" && "מנהל אתר"}
              {user?.permission === "admin" && "מנהל"}
              {user?.permission === "owner" && "בעלים"}
            </span>
            <Key size={14} className='key-icon' />
          </div>
          <button onClick={handleLogout} className='logout-button'>
            <LogOut size={18} />
            <span>יציאה</span>
          </button>
        </div>
      </div>

      {showPasswordModal && (
        <ChangePasswordModal onClose={() => setShowPasswordModal(false)} />
      )}

      {showMonthModal && (
        <MonthSelectionModal
          onClose={() => {
            setShowMonthModal(false);
            setPendingNavigation(null);
          }}
          onSelect={handleMonthSelected}
          title={pendingNavigation?.title}
        />
      )}
    </nav>
  );
};

export default Navbar;
